// Underdog bonus — display helper (mirrors backend).
// Two systems: FIFA-rank gap (tournaments) and Elo expected-probability (leagues).
// Authoritative scoring lives in the backend; this drives badges/legend only.

export const UNDERDOG_CAP = 8;

// FIFA rank-gap tiers (tournament mode): { maxGap, base, level, label }
const GAP_TIERS = [
  { max: 9, base: 0, level: "none", label: "No Underdog" },
  { max: 19, base: 2, level: "mild", label: "Mild Underdog" },
  { max: 39, base: 4, level: "clear", label: "Clear Underdog" },
  { max: Infinity, base: 6, level: "heavy", label: "Heavy Underdog" },
];

export const UNDERDOG_STYLES = {
  none: { dot: "bg-zinc-500", text: "text-zinc-400", border: "border-zinc-700", chip: "bg-zinc-800/50" },
  slight: { dot: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-500/50", chip: "bg-emerald-500/10" },
  mild: { dot: "bg-sky-400", text: "text-sky-300", border: "border-sky-500/50", chip: "bg-sky-500/10" },
  clear: { dot: "bg-amber-400", text: "text-amber-300", border: "border-amber-500/50", chip: "bg-amber-500/10" },
  heavy: { dot: "bg-orange-400", text: "text-orange-300", border: "border-orange-500/50", chip: "bg-orange-500/10" },
  massive: { dot: "bg-primary-accent", text: "text-primary-accent", border: "border-primary-accent/60", chip: "bg-primary-accent/10" },
};

function tierAdjustment(favRank) {
  if (favRank <= 10) return 2;
  if (favRank <= 25) return 1;
  return 0;
}

// Elo expected-probability tiers (league mode): bonus by underdog win probability.
function eloTier(p) {
  if (p < 0.10) return { base: 8, level: "massive", label: "Massive Underdog" };
  if (p < 0.20) return { base: 6, level: "heavy", label: "Heavy Underdog" };
  if (p < 0.30) return { base: 4, level: "clear", label: "Clear Underdog" };
  if (p < 0.40) return { base: 2, level: "mild", label: "Mild Underdog" };
  return { base: 1, level: "slight", label: "Slight Underdog" };
}

export function computeUnderdog(home, away, homeAdv = 75) {
  // Elo system (league) — used whenever both clubs carry an Elo rating.
  if (home?.elo && away?.elo) {
    const eff = home.elo + homeAdv;
    const eh = 1 / (1 + Math.pow(10, (away.elo - eff) / 400));
    const ea = 1 - eh;
    const ud = Math.min(eh, ea);
    if (ud >= 0.5) return null;
    const t = eloTier(ud);
    return {
      system: "elo",
      level: t.level,
      label: t.label,
      base: t.base,
      tierAdjustment: 0,
      finalBonus: t.base,
      underdogSide: eh < ea ? "home" : "away",
      underdogPct: Math.round(ud * 100),
      favouritePct: Math.round(Math.max(eh, ea) * 100),
    };
  }
  // FIFA rank-gap system (tournament)
  const hr = home?.fifa_rank;
  const ar = away?.fifa_rank;
  if (!hr || !ar || hr === ar) return null;
  const gap = Math.abs(hr - ar);
  const tier = GAP_TIERS.find((t) => gap <= t.max);
  const favRank = Math.min(hr, ar);
  const adj = tierAdjustment(favRank);
  const finalBonus = Math.min(tier.base + adj, UNDERDOG_CAP);
  return {
    system: "fifa",
    rankGap: gap,
    base: tier.base,
    level: tier.level,
    label: tier.label,
    tierAdjustment: adj,
    finalBonus,
    underdogSide: hr > ar ? "home" : "away",
    favRank,
  };
}

export const UNDERDOG_LEGEND = [
  { level: "none", label: "No Underdog", range: "Rank gap 0–9", base: 0 },
  { level: "mild", label: "Mild Underdog", range: "Rank gap 10–19", base: 2 },
  { level: "clear", label: "Clear Underdog", range: "Rank gap 20–39", base: 4 },
  { level: "heavy", label: "Heavy Underdog", range: "Rank gap 40+", base: 6 },
];

export const UNDERDOG_LEGEND_ELO = [
  { level: "slight", label: "Slight Underdog", range: "40–49.9% win prob", base: 1 },
  { level: "mild", label: "Mild Underdog", range: "30–39.9% win prob", base: 2 },
  { level: "clear", label: "Clear Underdog", range: "20–29.9% win prob", base: 4 },
  { level: "heavy", label: "Heavy Underdog", range: "10–19.9% win prob", base: 6 },
  { level: "massive", label: "Massive Underdog", range: "<10% win prob", base: 8 },
];
